"use client";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Collapsible,
  CollapsibleContent,
} from "@repo/ui";
import { ArrowRight, Menu, X } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useLayoutEffect, useRef, useState } from "react";

import { DocsDropdown } from "@/components/dashboard/docs-dropdown";
import { UserNav } from "@/components/dashboard/user-nav";
import NotificationDropdown from "@/components/ui/notification-dropdown";
import { useSession } from "@/lib/auth-client";
import { cn } from "@/lib/utils";
import type { WhatsNewNavItem } from "@/lib/whats-new";

import { HERO_HEADER_HEIGHT } from "./hero-header-spacing";
import { MarketingNavMenu } from "./marketing-nav-menu";
import { BOOK_A_CALL_HREF, mobileNavLinks } from "./nav-links";

const MORPH_TRAVEL = 72;
const FOLLOW_TAU = 90;

// Not 0: a fill arriving before the corners round lands as a square slab over
// the hero's pattern.
const FILL_AT = 0.5;

// Tailwind's `lg`, past which the menu and its trigger stop rendering.
const DESKTOP_QUERY = "(min-width: 64rem)";

const MORPH_ENDPOINTS = [
  HERO_HEADER_HEIGHT,
  "[--nav-h-to:3.5rem]",
  "[--nav-w-from:42rem] [--nav-w-to:42rem]",
  "[--nav-px-from:1.5rem] [--nav-px-to:0.875rem]",
  "lg:[--nav-w-from:80rem] lg:[--nav-w-to:60rem]",
  "lg:[--nav-px-from:2rem] lg:[--nav-px-to:1rem]",
].join(" ");

const between = (from: string, to: string) =>
  `calc(var(${from}) + (var(${to}) - var(${from})) * var(--nav-p))`;

// A reload restores its scroll position; a passive effect paints the docked
// shape first and snaps a frame later.
const useIsomorphicLayoutEffect =
  typeof window === "undefined" ? useEffect : useLayoutEffect;

export default function HeroHeaderClient({
  whatsNew,
  signInHref = "/login",
}: {
  whatsNew: WhatsNewNavItem | null;
  signInHref?: string;
}) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const { data: session } = useSession();
  const isSignedIn = Boolean(session);

  useIsomorphicLayoutEffect(() => {
    const header = headerRef.current;
    if (!header) {
      return;
    }

    const tracksScrollExactly = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    let frame = 0;
    let lastTime = 0;
    let progress = 0;
    let fill = "";

    const targetProgress = () => {
      const traveled = Math.min(1, Math.max(0, window.scrollY / MORPH_TRAVEL));
      return traveled * traveled * (3 - 2 * traveled);
    };

    const commit = (next: number) => {
      progress = next;
      header.style.setProperty("--nav-p", next.toFixed(4));
      const nextFill = next > FILL_AT ? "1" : "0";
      if (nextFill !== fill) {
        fill = nextFill;
        header.style.setProperty("--nav-fill", nextFill);
      }
    };

    const step = (time: number) => {
      const target = targetProgress();
      // Clamped so a backgrounded tab does not resume with one enormous step.
      const elapsed = lastTime ? Math.min(time - lastTime, 64) : 16;
      lastTime = time;

      const catchUp = tracksScrollExactly
        ? 1
        : 1 - Math.exp(-elapsed / FOLLOW_TAU);
      const next = progress + (target - progress) * catchUp;

      if (Math.abs(target - next) < 0.001) {
        commit(target);
        frame = 0;
        lastTime = 0;
        return;
      }

      commit(next);
      frame = requestAnimationFrame(step);
    };

    const onScroll = () => {
      frame ||= requestAnimationFrame(step);
    };

    commit(targetProgress());
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", onScroll);
      if (frame) {
        cancelAnimationFrame(frame);
      }
    };
  }, []);

  // Past lg the hamburger is hidden, so a menu left open while the window grows
  // could never be closed again.
  useEffect(() => {
    const desktop = window.matchMedia(DESKTOP_QUERY);
    const closeOnDesktop = () => {
      if (desktop.matches) {
        setIsMobileMenuOpen(false);
      }
    };

    desktop.addEventListener("change", closeOnDesktop);
    return () => {
      desktop.removeEventListener("change", closeOnDesktop);
    };
  }, []);

  return (
    // Sits above the hero card, pulled back over it by a negative margin: the
    // card's overflow-hidden would scope a sticky child to the card.
    <header
      ref={headerRef}
      className={cn(
        "pointer-events-none sticky z-50 [--nav-fill:0] [--nav-p:0] print:hidden",
        MORPH_ENDPOINTS,
        // The open menu overlays the hero instead of pushing it down, so it
        // needs a fill of its own at any scroll position.
        isMobileMenuOpen ? "[--nav-open:1]" : "[--nav-open:0]",
      )}
      style={{
        top: "var(--impersonation-banner-height, 0px)",
        height: "var(--nav-h-from)",
        marginBottom: "calc(var(--nav-h-from) * -1)",
      }}
    >
      <div className="px-4">
        <div
          className="pointer-events-auto mx-auto"
          style={{
            maxWidth: between("--nav-w-from", "--nav-w-to"),
            paddingInline: between("--nav-px-from", "--nav-px-to"),
            // Not a margin: a top margin has nothing to collapse against here
            // and escapes the header, landing the pill flush with the viewport.
            transform: "translateY(calc(1rem * var(--nav-p)))",
            borderRadius: "calc(1.75rem * max(var(--nav-p), var(--nav-open)))",
            backgroundColor:
              "color-mix(in srgb, var(--color-marketing-blue) calc(max(var(--nav-fill), var(--nav-open)) * 100%), transparent)",
          }}
        >
          <div
            className="flex items-center justify-between"
            style={{ height: between("--nav-h-from", "--nav-h-to") }}
          >
            {/* Left side: Logo + Nav */}
            <div className="flex items-center gap-8">
              {/* Logo + Wordmark */}
              <Link
                href="/"
                aria-label="home"
                className="flex items-center gap-2 select-none"
              >
                <Image
                  src="/brand/wordmark-light.svg"
                  alt="Terac"
                  // Intrinsic SVG dimensions (viewBox 0 0 175 56). Must match the
                  // real aspect ratio: with h-8 w-auto the rendered width follows
                  // the ratio, so a mismatched declaration (was 120x40 = 3.0 vs the
                  // true 3.125) made the logo resize on decode and jitter the nav.
                  width={175}
                  height={56}
                  className="h-8 w-auto brightness-0 invert select-none"
                  draggable={false}
                />
              </Link>

              {/* Desktop Nav - left aligned */}
              <MarketingNavMenu variant="dark" whatsNew={whatsNew} />
            </div>

            {/* Right side - Desktop. The auth slot swaps from the signed-out CTAs
                to the signed-in account nav once the client session resolves (e.g.
                after Google One Tap on the homepage). Those two clusters have very
                different widths (~240px vs ~110px), so swapping them in place inside
                this right-anchored row shifted the layout. To keep the slot a fixed
                size across the swap, an invisible copy of the wider CTAs reserves
                the width while the live content is overlaid, right-aligned, on top. */}
            <div className="relative hidden lg:block">
              <div
                aria-hidden
                className="pointer-events-none invisible flex items-center gap-3"
              >
                <SignedOutCtas signInHref={signInHref} />
              </div>
              <div className="absolute inset-0 flex items-center justify-end gap-3">
                {isSignedIn ? (
                  <nav className="flex items-center gap-2">
                    <NotificationDropdown />
                    <DocsDropdown className="hidden md:flex" />
                    <UserNav />
                  </nav>
                ) : (
                  <SignedOutCtas signInHref={signInHref} />
                )}
              </div>
            </div>

            {/* Mobile hamburger button */}
            <button
              onClick={() => {
                setIsMobileMenuOpen(!isMobileMenuOpen);
              }}
              aria-label={isMobileMenuOpen ? "Close Menu" : "Open Menu"}
              className="relative z-20 -m-2.5 block cursor-pointer p-2.5 lg:hidden"
            >
              <Menu
                className={`size-6 text-white transition-[scale,rotate,opacity] duration-200 ${isMobileMenuOpen ? "scale-0 rotate-180 opacity-0" : "scale-100 rotate-0 opacity-100"}`}
              />
              <X
                className={`absolute inset-0 m-auto size-6 text-white transition-[scale,rotate,opacity] duration-200 ${isMobileMenuOpen ? "scale-100 rotate-0 opacity-100" : "scale-0 -rotate-180 opacity-0"}`}
              />
            </button>
          </div>

          {/* Mobile Menu */}
          <Collapsible open={isMobileMenuOpen}>
            <CollapsibleContent className="data-[state=closed]:animate-slide-up data-[state=open]:animate-slide-down overflow-hidden lg:hidden">
              <MobileMenu
                closeMenu={() => {
                  setIsMobileMenuOpen(false);
                }}
                isSignedIn={isSignedIn}
                signInHref={signInHref}
              />
            </CollapsibleContent>
          </Collapsible>
        </div>
      </div>
    </header>
  );
}

function SignedOutCtas({ signInHref }: { signInHref: string }) {
  return (
    <>
      <Link
        href={BOOK_A_CALL_HREF}
        className="inline-flex items-center gap-1.5 rounded-full border border-white/30 bg-transparent px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-white/10"
      >
        Book a demo
      </Link>
      <Link
        href={signInHref}
        className="text-marketing-blue flex items-center gap-2 rounded-full border border-white bg-white px-5 py-2.5 text-sm font-medium transition-colors hover:bg-white/90"
      >
        Sign in
        <ArrowRight className="h-4 w-4" />
      </Link>
    </>
  );
}

function MobileMenu({
  closeMenu,
  isSignedIn,
  signInHref,
}: {
  closeMenu: () => void;
  isSignedIn: boolean;
  signInHref: string;
}) {
  return (
    // Scrolls itself: it hangs off a pinned header, so the page cannot scroll
    // to reach its end.
    <nav
      className="overscroll-contain pt-2 pb-6 lg:hidden"
      style={{
        maxHeight: "calc(100svh - var(--nav-h-from) - 3.5rem)",
        overflowY: "auto",
      }}
    >
      <Accordion type="single" collapsible className="space-y-1">
        {mobileNavLinks.map((group) => (
          <AccordionItem
            key={group.groupName}
            value={group.groupName}
            className="border-b border-white/20"
          >
            <AccordionTrigger className="flex items-center justify-between py-3 text-lg font-medium text-white hover:no-underline [&>svg]:text-white! [&[data-state=open]]:text-white/80">
              {group.groupName}
            </AccordionTrigger>
            <AccordionContent className="pb-4">
              <ul className="space-y-1">
                {group.links.map((feature) => (
                  <li key={feature.href}>
                    <Link
                      href={feature.href}
                      onClick={closeMenu}
                      className="block py-2 text-base text-white/80 transition-colors hover:text-white"
                    >
                      {feature.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>

      {/* Mobile CTA buttons */}
      <div className="mt-6 flex flex-col gap-3">
        {isSignedIn ? (
          <Link
            href="/me"
            onClick={closeMenu}
            className="text-marketing-blue flex items-center justify-center gap-2 rounded-full border border-white bg-white px-5 py-3 text-sm font-medium transition-colors hover:bg-white/90"
          >
            Go to Dashboard
            <ArrowRight className="h-4 w-4" />
          </Link>
        ) : (
          <>
            <Link
              href={BOOK_A_CALL_HREF}
              onClick={closeMenu}
              className="flex items-center justify-center gap-2 rounded-full border border-white/30 bg-transparent px-5 py-3 text-sm font-medium text-white transition-colors hover:bg-white/10"
            >
              Book a demo
              <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              href={signInHref}
              onClick={closeMenu}
              className="text-marketing-blue flex items-center justify-center gap-2 rounded-full border border-white bg-white px-5 py-3 text-sm font-medium transition-colors hover:bg-white/90"
            >
              Sign in
              <ArrowRight className="h-4 w-4" />
            </Link>
          </>
        )}
      </div>
    </nav>
  );
}
